import { SenecDrawerContent } from "src/components/modal/senec-drawer/SenecDrawerContent";
import { StyledDrawer } from "src/components/modal/senec-drawer/SenecDrawer.styled";
import { TooltipDetails } from "src/components/utils/senec-tooltip/SenecTooltip";
import { useScreenSize } from "src/hooks/useScreenSize";
import { ReactElement } from "react";
import { useTheme } from "@mui/material";
import { SenecBaseTheme } from "src/styles/senecBaseTheme";

export type Anchor = "bottom" | "left" | "right" | "top";

export interface SenecDrawerProps {
    /**
     * data-testid - id to find component in render tests
     */
    "data-testid"?: string;

    /**
     * title - header title
     */
    title: string;

    /**
     * isOpen - show drawer
     */
    isOpen: boolean;

    /**
     * onClose - callback onClose
     */
    onClose: () => void;

    /**
     * onBackdropClose - callback onBackdropClose
     */
    onBackdropClose?: () => void;

    /**
     * tooltip - tooltip details to display next to title
     */
    tooltip?: TooltipDetails;

    /**
     * TopComponent - component which will be shown in header below title
     */
    TopComponent?: ReactElement;

    /**
     * MainComponent - component rendererd below top component, scrollable
     */
    MainComponent?: ReactElement;

    /**
     * BottomComponent - component rendererd below top component, scrollable
     */
    BottomComponent?: ReactElement;

    /**
     * anchor - side from where drawer will slide in
     */
    anchor?: Anchor;

    /**
     * showDivider - show divider below main content
     */
    showDivider?: boolean;

    /**
     * width - the width of the drawer
     */
    width?: string;

    /**
     * min width - the min width of the drawer
     */
    minWidth?: string;

    /**
     * invisible backdrop - represents the visibility of the backdrop element
     */
    invisibleBackdrop?: boolean;

    /**
     * HeaderActionsComponent - Optional property representing additional headerActions
     */
    HeaderActionsComponent?: ReactElement;

    /**
     * variant - Optional MUI Drawer property
     * @default "temporary"
     */
    variant?: "persistent" | "permanent" | "temporary" | undefined;

    /**
     * Drawer component is rendered independent of open state by default
     * See https://mui.com/material-ui/react-drawer/#keep-mounted
     */
    keepMounted?: boolean;
}

export const SenecDrawer = ({
    title,
    tooltip,
    isOpen,
    onClose,
    onBackdropClose,
    TopComponent,
    MainComponent,
    anchor = "right",
    BottomComponent,
    showDivider = true,
    width,
    minWidth,
    invisibleBackdrop = false,
    "data-testid": dataTestId = "senec-drawer",
    HeaderActionsComponent,
    variant = "temporary",
    keepMounted = true,
}: SenecDrawerProps) => {
    const theme: SenecBaseTheme = useTheme();
    const { isMobile } = useScreenSize();

    const onCloseModal = (reason: "backdropClick" | string) => {
        if (onBackdropClose && reason === "backdropClick") {
            onBackdropClose();
        } else {
            onClose();
        }
    };

    return (
        <StyledDrawer
            theme={theme}
            anchor={isMobile ? "bottom" : anchor}
            variant={variant}
            open={isOpen}
            onOpen={() => {}}
            onClose={onClose}
            disableDiscovery={false}
            drawerWidth={width}
            drawerMinWidth={minWidth}
            slotProps={{ backdrop: { invisible: invisibleBackdrop } }}
            ModalProps={{ onClose: (_, reason) => onCloseModal(reason), keepMounted: keepMounted }}
        >
            {title && (
                <SenecDrawerContent
                    data-testid={dataTestId}
                    tooltip={tooltip}
                    TopComponent={TopComponent}
                    MainComponent={MainComponent}
                    BottomComponent={BottomComponent}
                    closeDrawerOnClick={() => onClose()}
                    title={title}
                    showDivider={showDivider}
                    HeaderActionsComponent={HeaderActionsComponent}
                />
            )}
        </StyledDrawer>
    );
};
